import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import { fetchArticles, fetchArticleFilters } from "@/lib/articles";

const SITE_URL = "https://mujeres-motor.com";
const RESOURCES_HERO = "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/159a9463b1718716bd94252135a312dd43c4b3cad30d8a8feef1af4da2de7514.jpeg";

const CATEGORY_LABELS = {
  "guía": "Guía", "opinión": "Opinión", "entrevista": "Entrevista",
  "toolkit": "Toolkit", "oportunidad": "Oportunidad",
};

function ArticleCard({ a }) {
  return (
    <Link to={`/resources/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group flex flex-col h-full">
      <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-charcoal-soft mb-5">
        {a.cover_image_url && <img src={a.cover_image_url} alt={a.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-transparent" />
        <span className="absolute top-4 left-4 bg-cream text-charcoal px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em]">{CATEGORY_LABELS[a.category] || a.category}</span>
      </div>
      <h3 className="font-serif text-xl md:text-2xl text-charcoal font-light leading-tight mb-3 group-hover:text-terracotta transition-colors">{a.title}</h3>
      <p className="text-sm text-charcoal/70 line-clamp-3 mb-4 flex-1">{a.excerpt}</p>
      <div className="flex items-center justify-between text-xs text-charcoal/55">
        <span>{a.author_name}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{a.reading_time_minutes} min</span>
      </div>
    </Link>
  );
}

export default function Resources() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticleFilters().then((f) => setCategories(f.categories || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchArticles(filter ? { category: filter } : {}).then((list) => { setArticles(list); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);

  return (
    <SiteLayout transparent>
      <></>

      <section data-testid="resources-hero" className="relative min-h-[55vh] flex items-end bg-charcoal text-cream overflow-hidden pb-14">
        <img src={RESOURCES_HERO} alt="Recursos editoriales" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/50 to-charcoal" />
        <Container className="relative z-10">
          <Eyebrow tone="gold" className="mb-5 block">Recursos</Eyebrow>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">Lecturas, guías y <span className="italic">oportunidades.</span></h1>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl">Editoriales cortos, toolkits prácticos y directorios de oportunidad para mujeres líderes y marcas aliadas.</p>
        </Container>
      </section>

      {/* Filters */}
      <section className="bg-cream sticky top-[72px] z-20 py-5 border-b border-charcoal/10" data-testid="resources-filters">
        <Container>
          <div className="flex flex-wrap gap-2 overflow-x-auto">
            <button onClick={() => setFilter("")} data-testid="filter-all" className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${filter === "" ? "bg-charcoal text-cream" : "border border-charcoal/15 text-charcoal/70 hover:border-charcoal"}`}>
              Todos
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${filter === c ? "bg-charcoal text-cream" : "border border-charcoal/15 text-charcoal/70 hover:border-charcoal"}`}>
                {CATEGORY_LABELS[c] || c}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* Grid */}
      <section className="bg-cream py-20 md:py-24">
        <Container>
          {loading ? (
            <p className="text-charcoal/50">Cargando…</p>
          ) : articles.length === 0 ? (
            <p className="font-serif italic text-xl text-charcoal/60 py-16 text-center">Sin artículos en esta categoría.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {articles.map((a) => <ArticleCard key={a.id} a={a} />)}
            </div>
          )}
        </Container>
      </section>
    </SiteLayout>
  );
}
